// Wrapper for the Web Notifications API. Lets the app show OS-level
// notifications (macOS Notification Center, Windows Action Center)
// while a browser tab/window is open. Web Push (when the tab is
// closed) lives in lib/web-push.ts and uses a service worker.

export type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported'

export function getPermission(): PermissionState {
  if (typeof window === 'undefined') return 'unsupported'
  if (!('Notification' in window)) return 'unsupported'
  return window.Notification.permission as PermissionState
}

export async function requestPermission(): Promise<PermissionState> {
  if (typeof window === 'undefined') return 'unsupported'
  if (!('Notification' in window)) return 'unsupported'
  try {
    const result = await window.Notification.requestPermission()
    return result as PermissionState
  } catch {
    return 'denied'
  }
}

interface ShowOsNotificationOptions {
  /** Used by the OS to deduplicate consecutive notifications. Pass an event_id or thread id. */
  tag?: string
  /** URL to open on click. */
  url?: string
  /**
   * In-app navigation for `url`. When provided it receives the app-relative
   * path instead of `window.location.assign`, which hard-reloads the SPA and
   * drops all state on every OS-notification click.
   */
  onNavigate?: (path: string) => void
  /** Optional icon URL. Falls back to the favicon. */
  icon?: string
  /** If true, fires even when the document has focus. Default false. */
  force?: boolean
}

/**
 * Shows an OS-level notification. No-op if permission is not granted
 * or if the document is currently visible (unless `force` is true).
 * Returns the Notification instance for advanced uses, or null.
 */
export function showOsNotification(
  title: string,
  body: string,
  options: ShowOsNotificationOptions = {}
): Notification | null {
  if (typeof window === 'undefined') return null
  if (!('Notification' in window)) return null
  if (window.Notification.permission !== 'granted') return null

  // Don't disturb if the user is actively looking at the app, unless caller insists.
  if (!options.force && typeof document !== 'undefined' && document.visibilityState === 'visible') {
    return null
  }

  try {
    const n = new window.Notification(title, {
      body,
      tag: options.tag,
      icon: options.icon ?? '/favicon.ico',
      badge: '/favicon.ico',
    })
    if (options.url) {
      n.onclick = () => {
        window.focus()
        const url = new URL(options.url!, window.location.origin)
        if (options.onNavigate && url.origin === window.location.origin) {
          options.onNavigate(url.pathname + url.search + url.hash)
        } else {
          window.location.assign(options.url!)
        }
        n.close()
      }
    }
    return n
  } catch (err) {
    console.warn('Failed to show OS notification:', err)
    return null
  }
}
